import { useMemo, useRef, useState } from "react";
import { Copy, MapPin, MessageCircle } from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import { Button } from "@/components/ui/button";
import { logo } from "@/assets";
import { useSession } from "@/contexts/AuthContext";
import {
  copyToClipboard,
  isNativePlatform,
  saveAndShareFile,
  shareImageWithText,
} from "@/lib/capacitor";
import { routes } from "@/lib/constants";
import { inter, philosopher } from "@/lib/fonts";
import { cn } from "@/lib/utils";

interface ReferralQrViewProps {
  onBack: () => void;
}

function buildReferralLink(referralCode: string) {
  if (typeof window === "undefined") {
    return `${routes.auth.signup}?referralCode=${encodeURIComponent(referralCode)}`;
  }

  return `${window.location.origin}${routes.auth.signup}?referralCode=${encodeURIComponent(referralCode)}`;
}

function buildReferralShareMessage(referralLink: string, referralCode: string) {
  return `Join Mumin's Mart using my referral link:\n${referralLink}\n\nReferral code: ${referralCode}`;
}

function buildLocationLabel(
  addressLine?: string,
  city?: string,
  state?: string,
) {
  return [addressLine, city, state].filter(Boolean).join(", ");
}

async function exportElementAsPng(element: HTMLElement) {
  const { toPng } = await import("html-to-image");

  return toPng(element, {
    cacheBust: true,
    pixelRatio: 2,
    backgroundColor: "#ffffff",
  });
}

async function downloadDataUrl(dataUrl: string, filename: string) {
  if (isNativePlatform()) {
    const base64 = dataUrl.split(",")[1];

    if (!base64) {
      throw new Error("Invalid image data");
    }

    await saveAndShareFile({
      filename,
      data: base64,
      mimeType: "image/png",
    });
    return;
  }

  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = filename;
  anchor.click();
}

const ReferralQrView = ({ onBack }: ReferralQrViewProps) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharingOnWhatsApp, setIsSharingOnWhatsApp] = useState(false);
  const { data: session } = useSession();
  const referralCode = session?.user?.referralCode?.trim() ?? "";
  const seller = session?.user?.sellers?.[0];

  const displayName = seller?.name ?? session?.user?.fullName ?? "My Referrals";
  const location = buildLocationLabel(
    seller?.addressLine1,
    seller?.city,
    seller?.state,
  );
  const referralLink = useMemo(
    () => (referralCode ? buildReferralLink(referralCode) : ""),
    [referralCode],
  );

  const handleCopyReferralCode = async () => {
    if (!referralCode) {
      toast.error("Referral code not available");
      return;
    }

    const copied = await copyToClipboard(referralCode);

    if (copied) {
      toast.success("Referral code copied");
      return;
    }

    toast.error("Unable to copy referral code");
  };

  const handleShareOnWhatsApp = async () => {
    if (!cardRef.current || !referralLink || !referralCode) {
      toast.error("Referral link not available");
      return;
    }

    setIsSharingOnWhatsApp(true);
    const shareMessage = buildReferralShareMessage(referralLink, referralCode);

    try {
      const dataUrl = await exportElementAsPng(cardRef.current);
      const base64 = dataUrl.split(",")[1];

      if (!base64) {
        throw new Error("Invalid image data");
      }

      const shared = await shareImageWithText({
        filename: `referral-${referralCode}.png`,
        data: base64,
        mimeType: "image/png",
        text: shareMessage,
        title: "My Referral",
        dialogTitle: "Share on WhatsApp",
      });

      if (!shared) {
        toast.error("Sharing was cancelled");
        return;
      }

      toast.success(
        isNativePlatform()
          ? "Message copied. Paste it as the caption in WhatsApp."
          : "Referral card ready to share",
      );
    } catch {
      toast.error("Unable to share on WhatsApp");
    } finally {
      setIsSharingOnWhatsApp(false);
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current || !referralLink) {
      return;
    }

    setIsDownloading(true);

    try {
      const dataUrl = await exportElementAsPng(cardRef.current);
      const filename = `referral-${referralCode || "code"}.png`;
      await downloadDataUrl(dataUrl, filename);
      toast.success(
        isNativePlatform()
          ? "Referral card ready to save or share"
          : "Referral card downloaded",
      );
    } catch {
      toast.error("Unable to download referral card");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen pb-28">
      <ButtonsHeader
        heading="My Referrals"
        showButtons={false}
        onBack={onBack}
      />

      <div className="flex justify-center">
        <div className="w-full max-w-sm overflow-hidden rounded-4xl border border-[#ECE5DC] bg-white shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
          <div ref={cardRef}>
            <div className="space-y-2 p-5 text-center">
              <img
                src={logo}
                alt="Mumin's Mart"
                width={72}
                height={72}
                className="h-16 w-auto object-contain mx-auto"
              />
              <h2
                className={`text-xl font-bold text-[#1A1A1A] ${philosopher.className}`}
              >
                {displayName}
              </h2>

              {location ? (
                <div
                  className={`flex items-center justify-center gap-1.5 text-sm text-[#8A8A8A] ${inter.className}`}
                >
                  <MapPin className="size-4 shrink-0" strokeWidth={1.5} />
                  <span>{location}</span>
                </div>
              ) : null}

              <div className="flex justify-center">
                {referralLink ? (
                  <div className="rounded-2xl bg-white p-3">
                    <QRCode
                      value={referralLink}
                      size={220}
                      level="H"
                      bgColor="#FFFFFF"
                      fgColor="#000000"
                    />
                  </div>
                ) : (
                  <div className="flex size-[220px] items-center justify-center rounded-2xl bg-[#F5F5F5] text-sm text-[#8A8A8A]">
                    Referral code unavailable
                  </div>
                )}
              </div>

              <div className="relative flex items-center justify-center rounded-full border border-dashed border-[#D9D9D9] bg-white px-12 py-3.5">
                <span
                  className={`text-lg font-bold tracking-[0.2em] text-[#1A1A1A] ${inter.className}`}
                >
                  {referralCode || "—"}
                </span>
                <button
                  type="button"
                  aria-label="Copy referral code"
                  disabled={!referralCode}
                  onClick={() => {
                    void handleCopyReferralCode();
                  }}
                  className="absolute right-4 rounded-full p-1 text-[#6F6F6F] transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Copy className="size-5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-20 rounded-t-4xl border-t border-[#F0F0F0] bg-white px-4 py-5">
        <div className="mx-auto flex max-w-lg gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void handleShareOnWhatsApp();
            }}
            disabled={!referralLink || isSharingOnWhatsApp}
            className={cn(
              "h-12 flex-1 rounded-full border-[#25D366] bg-white text-base font-semibold text-[#128C7E] hover:bg-[#25D366]/5 disabled:opacity-50",
              inter.className,
            )}
          >
            <MessageCircle className="size-5 text-[#25D366]" />
            {isSharingOnWhatsApp ? "Opening..." : "WhatsApp"}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void handleDownload();
            }}
            disabled={!referralLink || isDownloading}
            className={cn(
              "h-12 flex-1 rounded-full bg-secondary text-base font-semibold text-white hover:bg-secondary/90 disabled:opacity-50",
              inter.className,
            )}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReferralQrView;
